import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAffAuth, AFF_API } from "../context/AffAuthContext";
import {
  ShieldCheck, EnvelopeSimple, Lock, ArrowRight, Phone,
  Bank, CheckCircle, Clock, IdentificationCard, WhatsappLogo, Briefcase,
  ChartLineUp, FileText, ArrowLeft, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const TRADING_EXP_OPTIONS = [
  { value: "0_years", label: "No experience" },
  { value: "1_year", label: "1 year" },
  { value: "2_years", label: "2 years" },
  { value: "3_years", label: "3 years" },
  { value: "3plus_years", label: "3+ years" },
];
const WORK_OPTIONS = [
  { value: "student", label: "Student" },
  { value: "job_seeker", label: "Job Seeker" },
  { value: "job", label: "Job" },
  { value: "business", label: "Business" },
];

const validUpi = (v) => /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64}$/.test(v || "");

export default function AffLoginPage() {
  const { user, login } = useAffAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";

  const [isRegister, setIsRegister] = useState(!!refCode);
  const [step, setStep] = useState(0); // 0 Citizenship, 1 Aadhaar (Indian only), 2 Phone, 3 Email, 4 Profile, 5 Pay
  const [citizenship, setCitizenship] = useState(""); // "indian" | "non_indian"
  const [country, setCountry] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState("");
  // Address-slot ladder failure: server sends a structured object detail.
  // We capture it here so the UI can render the sponsor-inactive banner and
  // offer the "I don't have a referral code" checkbox that lets the applicant
  // continue as an independent registrant (no family ties).
  const [addressLock, setAddressLock] = useState(null);
  const [acceptDropRef, setAcceptDropRef] = useState(false);
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showSignupPwd, setShowSignupPwd] = useState(false);

  // ---- Aadhaar step ----
  const [aadhaar, setAadhaar] = useState("");
  const [aadhaarSessionId, setAadhaarSessionId] = useState("");
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
  const [aadhaarOtp, setAadhaarOtp] = useState("");
  const [aadhaarToken, setAadhaarToken] = useState("");
  const [autofill, setAutofill] = useState(null); // {name,age,sex,address}
  const [aadhaarSending, setAadhaarSending] = useState(false);
  const [aadhaarVerifying, setAadhaarVerifying] = useState(false);

  // ---- Phone step ----
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [whatsappSame, setWhatsappSame] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneToken, setPhoneToken] = useState("");
  const [phoneExpiresAt, setPhoneExpiresAt] = useState(0);
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);

  // ---- Email step ----
  const [email, setEmail] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailToken, setEmailToken] = useState("");
  const [emailExpiresAt, setEmailExpiresAt] = useState(0);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);

  // ---- Profile step ----
  const [password, setPassword] = useState("");
  const [upiId, setUpiId] = useState("");
  const [work, setWork] = useState("");
  const [tradingExp, setTradingExp] = useState("");
  const [referral, setReferral] = useState(refCode);
  const [useDefaultRef, setUseDefaultRef] = useState(false);
  const [defaultRef, setDefaultRef] = useState("");

  // ---- T&C checkbox ----
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // ---- Live tick for OTP timers ----
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { if (user) navigate("/affiliate/dashboard", { replace: true }); }, [user, navigate]);

  useEffect(() => {
    if (!isRegister) return;
    axios.get(`${AFF_API}/affiliate/public/default-referral-code`)
      .then((r) => setDefaultRef(r.data?.code || ""))
      .catch(() => setDefaultRef(""));
  }, [isRegister]);

  const phoneSecondsLeft = Math.max(0, Math.ceil((phoneExpiresAt - tick) / 1000));
  const emailSecondsLeft = Math.max(0, Math.ceil((emailExpiresAt - tick) / 1000));

  // ============================================================
  // STEP 1: AADHAAR
  // ============================================================
  const sendAadhaarOtp = async () => {
    setError("");
    if ((aadhaar || "").replace(/\D/g, "").length !== 12) {
      setError("Enter all 12 digits of your Aadhaar"); return;
    }
    setAadhaarSending(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/auth/aadhaar/send-otp`, { aadhaar });
      setAadhaarOtpSent(true);
      if (res.data.sessionId || res.data.session_id) {
        setAadhaarSessionId(res.data.sessionId || res.data.session_id);
      }
      toast.success(res.data.stub ? "Aadhaar OTP sent (dev: 123456)" : (res.data.message || "Aadhaar OTP sent successfully"));
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not send OTP");
    } finally { setAadhaarSending(false); }
  };

  const verifyAadhaarOtp = async () => {
    setError("");
    if (aadhaarOtp.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setAadhaarVerifying(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/auth/aadhaar/verify-otp`, {
        aadhaar,
        otp: aadhaarOtp,
        sessionId: aadhaarSessionId,
      });
      setAadhaarToken(res.data.aadhaar_otp_token);
      setAutofill(res.data.autofill);
      toast.success(`Aadhaar verified · Welcome ${res.data.autofill?.name}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Aadhaar verification failed");
    } finally { setAadhaarVerifying(false); }
  };

  // ============================================================
  // STEP 2: PHONE
  // ============================================================
  const sendPhoneOtp = async () => {
    setError("");
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 14) { setError("Enter a valid phone number"); return; }
    const fullPhone = `${countryCode}${digits}`;
    setPhoneSending(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/auth/send-phone-otp`, { phone: fullPhone });
      setPhoneOtpSent(true);
      setPhoneExpiresAt(Date.now() + (res.data.expires_in_seconds || 600) * 1000);
      toast.success(res.data.stub ? "OTP sent (dev: 123456)" : `OTP sent to ${fullPhone}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not send OTP");
    } finally { setPhoneSending(false); }
  };

  const verifyPhoneOtp = async () => {
    setError("");
    setPhoneVerifying(true);
    try {
      const fullPhone = `${countryCode}${phone.replace(/\D/g, "")}`;
      const res = await axios.post(`${AFF_API}/affiliate/auth/verify-phone-otp`, { phone: fullPhone, otp: phoneOtp });
      setPhoneToken(res.data.phone_otp_token);
      toast.success("Phone verified");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "OTP verification failed");
    } finally { setPhoneVerifying(false); }
  };

  // ============================================================
  // STEP 3: EMAIL
  // ============================================================
  const sendEmailOtp = async () => {
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid email"); return; }
    setEmailSending(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/auth/send-email-otp`, { email });
      setEmailOtpSent(true);
      setEmailExpiresAt(Date.now() + (res.data.expires_in_seconds || 600) * 1000);
      toast.success(res.data.stub ? "OTP sent (dev: 123456)" : `OTP sent to ${email}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not send OTP");
    } finally { setEmailSending(false); }
  };

  const verifyEmailOtp = async () => {
    setError("");
    setEmailVerifying(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/auth/verify-email-otp`, { email, otp: emailOtp });
      setEmailToken(res.data.email_otp_token);
      toast.success("Email verified");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "OTP verification failed");
    } finally { setEmailVerifying(false); }
  };

  // ============================================================
  // STEP 5: PAY
  // ============================================================
  const loadCashfreeScript = () => new Promise((resolve) => {
    if (window.Cashfree) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const finishSignupWith = async (orderId, paymentId, signature) => {
    const res = await axios.post(`${AFF_API}/affiliate/auth/signup-verify`, {
      order_id: orderId,
      cashfree_order_id: orderId,
      razorpay_order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    });
    login(res.data.user, res.data.session_token);
    toast.success("Account created! Welcome to VeloxAlgoTech LLP.");
    navigate("/affiliate/dashboard", { replace: true });
  };

  const handlePayAndRegister = async () => {
    setError("");
    if (!acceptedTerms) { setError("Please accept the Terms & Conditions"); return; }
    setLoading(true);
    try {
      const payload = {
        email, password, phone,
        whatsapp_number: whatsappSame ? phone : whatsappNumber,
        whatsapp_same_as_phone: whatsappSame,
        trading_experience: tradingExp,
        work,
        upi_id: upiId,
        accepted_terms: true,
        phone_otp_token: phoneToken,
        email_otp_token: emailToken,
        aadhaar_otp_token: aadhaarToken,
        citizenship,
        full_name: citizenship === "non_indian" ? fullName : undefined,
        country: citizenship === "non_indian" ? country : undefined,
      };
      if (useDefaultRef) payload.use_default_referral = true;
      else payload.referral_code = referral || undefined;

      const orderRes = await axios.post(`${AFF_API}/affiliate/auth/signup-order`, payload);
      const { order_id, payment_session_id, environment, gateway, stub, amount_paise, key_id, prefill } = orderRes.data;

      if (stub) {
        await finishSignupWith(order_id, `stub_pay_${Date.now()}`, null);
        return;
      }

      if (gateway === "cashfree" && payment_session_id) {
        const ok = await loadCashfreeScript();
        if (!ok) { setError("Cashfree Payment SDK failed to load"); setLoading(false); return; }
        const cashfree = window.Cashfree({ mode: environment === "production" ? "production" : "sandbox" });
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_modal",
        }).then(async (result) => {
          if (result.error) {
            setError(result.error.message || "Payment cancelled / failed");
            setLoading(false);
            return;
          }
          if (result.paymentDetails || result.redirect === false) {
            try {
              await finishSignupWith(order_id, null, null);
            } catch (err) {
              const d = err.response?.data?.detail;
              setError(typeof d === "string" ? d : "Payment verification failed");
              setLoading(false);
            }
          }
        });
        return;
      }

      // Razorpay fallback
      const ok = await loadRazorpayScript();
      if (!ok) { setError("Razorpay SDK failed to load"); setLoading(false); return; }
      const rzp = new window.Razorpay({
        key: key_id, amount: amount_paise, currency: "INR",
        name: "VeloxAlgoTech LLP",
        description: citizenship === "non_indian" ? "Platform joining fee · $99 USD" : "Platform joining fee · ₹2,999",
        order_id, prefill,
        theme: { color: "#7DD3FC" },
        handler: async (response) => {
          try {
            await finishSignupWith(response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
          } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Payment verification failed");
          }
        },
        modal: { ondismiss: () => { toast.info("Payment cancelled. You can retry anytime."); setLoading(false); } },
      });
      rzp.open();
    } catch (err) {
      const d = err.response?.data?.detail;
      // Structured error from _can_add_second_account_at_address ladder rule
      if (d && typeof d === "object" && d.code && d.code.startsWith("ADDRESS_")) {
        setAddressLock(d);
        // Reset the "drop ref" acknowledgement so user has to consciously check it
        setAcceptDropRef(false);
        setError(d.message || "Address slot locked");
      } else {
        setError(typeof d === "string" ? d : (d?.message || "Signup failed"));
      }
      setLoading(false);
    }
  };

  // ============================================================
  // LOGIN
  // ============================================================
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/auth/login`, { email: loginEmail, password: loginPassword });
      login(res.data.user, res.data.session_token);
      navigate("/affiliate/dashboard", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/affiliate/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ============================================================
  // RENDER
  // ============================================================
  const stepHeaders = citizenship === "non_indian"
    ? [
        { n: 0, label: "Citizen", icon: ShieldCheck },
        { n: 2, label: "Phone",   icon: Phone },
        { n: 3, label: "Email",   icon: EnvelopeSimple },
        { n: 4, label: "Profile", icon: Briefcase },
        { n: 5, label: "Pay",     icon: ShieldCheck },
      ]
    : [
        { n: 0, label: "Citizen", icon: ShieldCheck },
        { n: 1, label: "Aadhaar", icon: IdentificationCard },
        { n: 2, label: "Phone",   icon: Phone },
        { n: 3, label: "Email",   icon: EnvelopeSimple },
        { n: 4, label: "Profile", icon: Briefcase },
        { n: 5, label: "Pay",     icon: ShieldCheck },
      ];

  return (
    <div
      className="min-h-screen flex items-center justify-center py-8 relative overflow-hidden"
      style={{ background: "var(--velox-bg)" }}
      data-testid="aff-login-page"
    >
      {/* Ambient background — matches landing page hero */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#00b4d8]/8 blur-[140px]" />
        <div className="absolute -bottom-52 -right-40 w-[620px] h-[620px] rounded-full bg-[#0077b6]/10 blur-[150px]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
      </div>

      {/* Brand lockup — top-left, mirrors landing nav */}
      <Link
        to="/"
        className="absolute top-6 left-6 md:top-8 md:left-10 flex items-center gap-3 z-10"
        data-testid="login-brand-lockup"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png"
          alt="VeloxAlgoTech LLP"
          className="w-12 h-12 rounded-full ring-1"
          style={{ ringColor: "#00b4d8" }}
        />
        <span
          className="text-xs md:text-sm font-extrabold tracking-[0.14em] leading-[1.1] flex flex-col"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="brand-name-login"
        >
          <span style={{ color: '#00b4d8' }}>Velox</span>
          <span className="text-white">AlgoTech</span>
        </span>
      </Link>

      <div
        className="w-full max-w-lg mx-4 p-8 rounded-2xl relative z-10"
        style={{
          background: "linear-gradient(180deg, var(--velox-panel) 0%, var(--velox-bg-elevated) 100%)",
          border: "1px solid var(--velox-border-strong)",
          backdropFilter: "blur(24px)",
          boxShadow: "0 30px 80px -30px rgba(0,180,216,0.15)",
        }}
      >
        <div className="flex flex-col items-center mb-5">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
            style={{ background: "rgba(0,180,216,0.12)", border: "1px solid rgba(0,180,216,0.3)" }}
          >
            <ShieldCheck size={24} weight="duotone" style={{ color: "#00b4d8" }} />
          </div>
          <h1
            className="text-xl font-bold text-zinc-50 tracking-tight"
            style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}
          >
            {isRegister ? "Register" : "Login"}
          </h1>
          <p className="text-xs text-zinc-400 mt-1">
            {isRegister ? "Create your account" : "Sign in to your account"}
          </p>
        </div>

        {error && (
          <div className="mb-3 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-xs text-rose-400" data-testid="auth-error">{error}</div>
        )}

        {/* ---- LOGIN MODE ---- */}
        {!isRegister && (
          <>
            <form onSubmit={handleLogin} className="space-y-2.5">
              <div className="relative">
                <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input data-testid="login-email-input" type="email" placeholder="Email address" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm placeholder:text-zinc-600" />
              </div>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input data-testid="login-password-input" type={showLoginPwd ? "text" : "password"} placeholder="Password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required minLength={6} className="pl-9 pr-10 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm placeholder:text-zinc-600" />
                <button
                  type="button"
                  onClick={() => setShowLoginPwd((p) => !p)}
                  tabIndex={-1}
                  aria-label={showLoginPwd ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showLoginPwd ? <EyeSlash size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full py-2.5 rounded-lg font-bold text-white bg-[#00b4d8] hover:bg-[#0077b6] disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <>Sign In <ArrowRight size={14} weight="bold" /></>}
              </button>
            </form>
            <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-zinc-800" /><span className="text-[10px] text-zinc-500 uppercase">or</span><div className="flex-1 h-px bg-zinc-800" /></div>
            <button onClick={handleGoogle} data-testid="aff-google-login-btn" className="w-full py-2.5 px-4 rounded-lg font-medium text-zinc-300 border border-zinc-800 hover:border-zinc-700 hover:text-white flex items-center justify-center gap-3">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </button>
          </>
        )}

        {/* ---- REGISTER MODE ---- */}
        {isRegister && (
          <>
            {/* Step indicator */}
            <div className="mb-5 flex items-center justify-between" data-testid="signup-stepper">
              {stepHeaders.map((s, idx) => {
                const Icon = s.icon;
                const done = step > s.n;
                const active = step === s.n;
                return (
                  <div key={s.n} className="flex items-center flex-1">
                    <div className={`flex flex-col items-center ${active ? "text-sky-400" : done ? "text-emerald-400" : "text-zinc-600"}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${active ? "border-sky-400 bg-sky-500/10" : done ? "border-emerald-400 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                        {done ? <CheckCircle size={14} weight="fill" /> : <Icon size={12} weight="duotone" />}
                      </div>
                      <span className="text-[9px] font-semibold mt-1 uppercase tracking-wider">{s.label}</span>
                    </div>
                    {idx < stepHeaders.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${done ? "bg-emerald-500/60" : "bg-zinc-800"}`} />}
                  </div>
                );
              })}
            </div>

            {/* STEP 0: CITIZENSHIP */}
            {step === 0 && (
              <div className="space-y-3" data-testid="signup-step-citizenship">
                <div className="p-3 rounded-lg bg-sky-500/10 border border-sky-400/30">
                  <p className="text-[11px] text-sky-200 leading-relaxed">
                    Please tell us where you&apos;re registering from. This changes the verification steps required for your account.
                  </p>
                </div>

                <button
                  type="button"
                  data-testid="citizenship-indian-btn"
                  onClick={() => { setCitizenship("indian"); setCountry("India"); setCountryCode("+91"); setStep(1); }}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${citizenship === "indian" ? "border-emerald-400 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950 hover:border-emerald-500/50"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl leading-none">🇮🇳</div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-zinc-100">Indian Citizen</p>
                    </div>
                    {citizenship === "indian" && <CheckCircle size={16} weight="fill" className="text-emerald-400" />}
                  </div>
                </button>

                <button
                  type="button"
                  data-testid="citizenship-non-indian-btn"
                  onClick={() => { setCitizenship("non_indian"); setCountry(""); setCountryCode("+1"); setStep(2); }}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${citizenship === "non_indian" ? "border-amber-400 bg-amber-500/10" : "border-zinc-800 bg-zinc-950 hover:border-amber-500/50"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl leading-none">🌍</div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-zinc-100">Non-Indian Citizen</p>
                    </div>
                    {citizenship === "non_indian" && <CheckCircle size={16} weight="fill" className="text-amber-400" />}
                  </div>
                </button>
              </div>
            )}

            {/* STEP 1: AADHAAR */}
            {step === 1 && (
              <div className="space-y-3" data-testid="signup-step-aadhaar">
                <div className="p-3 rounded-lg bg-sky-500/10 border border-sky-400/30">
                  <p className="text-[11px] text-sky-200 leading-relaxed">
                    Start with your Aadhaar. We&apos;ll OTP-verify it (mock in dev) and auto-fill your Name, Age, Sex and Address from UIDAI. <span className="font-bold">One Aadhaar = one account only.</span>
                  </p>
                </div>
                <div className="relative">
                  <IdentificationCard size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    data-testid="signup-aadhaar-input"
                    placeholder="12-digit Aadhaar number"
                    inputMode="numeric"
                    maxLength={14}
                    value={aadhaar}
                    onChange={(e) => { setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12)); setAadhaarOtpSent(false); setAadhaarToken(""); setAutofill(null); }}
                    disabled={!!aadhaarToken}
                    className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm font-mono tracking-widest disabled:opacity-60"
                  />
                </div>
                {!aadhaarToken && (
                  <button type="button" onClick={sendAadhaarOtp} disabled={aadhaarSending || aadhaar.length !== 12} data-testid="aadhaar-send-otp-btn"
                    className="w-full py-2 rounded-md text-[11px] font-bold bg-[#00b4d8]/15 border border-[#00b4d8]/40 text-[#00b4d8] hover:bg-[#00b4d8]/25 disabled:opacity-40">
                    {aadhaarSending ? "Sending…" : aadhaarOtpSent ? "Resend Aadhaar OTP" : "Send Aadhaar OTP"}
                  </button>
                )}
                {aadhaarOtpSent && !aadhaarToken && (
                  <div className="flex gap-2" data-testid="aadhaar-otp-row">
                    <Input data-testid="aadhaar-otp-input" placeholder="6-digit OTP" maxLength={6} value={aadhaarOtp} onChange={(e) => setAadhaarOtp(e.target.value.replace(/\D/g, ""))}
                      className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm font-mono tracking-[0.4em]" />
                    <button type="button" onClick={verifyAadhaarOtp} disabled={aadhaarVerifying || aadhaarOtp.length !== 6} data-testid="aadhaar-verify-btn"
                      className="px-3 py-2 rounded-md text-[11px] font-bold bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 disabled:opacity-40">
                      {aadhaarVerifying ? "Verifying…" : "Verify"}
                    </button>
                  </div>
                )}
                {autofill && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-1.5" data-testid="aadhaar-autofill-card">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle size={12} weight="fill" className="text-emerald-400" />
                      <p className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">Aadhaar verified · Auto-filled</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] mt-1.5">
                      <div><span className="text-zinc-500">Name:</span> <span className="text-zinc-100 font-semibold" data-testid="autofill-name">{autofill.name || "—"}</span></div>
                      <div><span className="text-zinc-500">Age:</span> <span className="text-zinc-100 font-semibold" data-testid="autofill-age">{autofill.age || "—"}</span></div>
                      <div><span className="text-zinc-500">Sex:</span> <span className="text-zinc-100 font-semibold" data-testid="autofill-sex">{autofill.sex || "—"}</span></div>
                      <div><span className="text-zinc-500">DOB:</span> <span className="text-zinc-100 font-semibold">{autofill.dob || "—"}</span></div>
                    </div>
                    <div className="text-[11px]"><span className="text-zinc-500">Address:</span> <span className="text-zinc-100" data-testid="autofill-address">{autofill.address?.full}</span></div>
                  </div>
                )}
                {aadhaarToken && (
                  <button type="button" onClick={() => setStep(2)} data-testid="signup-next-step-1" className="w-full py-2.5 rounded-lg text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 flex items-center justify-center gap-2">
                    Continue to Phone <ArrowRight size={12} weight="bold" />
                  </button>
                )}
              </div>
            )}

            {/* STEP 2: PHONE + WHATSAPP */}
            {step === 2 && (
              <div className="space-y-3" data-testid="signup-step-phone">
                <div className="flex gap-2">
                  <select
                    data-testid="signup-country-code"
                    value={countryCode}
                    onChange={(e) => { setCountryCode(e.target.value); setPhoneToken(""); setPhoneOtpSent(false); }}
                    disabled={!!phoneToken}
                    className="w-[110px] bg-zinc-950 border border-zinc-800 rounded-md text-zinc-100 text-sm px-2 disabled:opacity-60"
                  >
                    <option value="+91" disabled={citizenship === "non_indian"}>🇮🇳 +91{citizenship === "non_indian" ? " (India — use Aadhaar path)" : ""}</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+65">🇸🇬 +65</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+974">🇶🇦 +974</option>
                    <option value="+966">🇸🇦 +966</option>
                    <option value="+60">🇲🇾 +60</option>
                    <option value="+63">🇵🇭 +63</option>
                    <option value="+62">🇮🇩 +62</option>
                    <option value="+66">🇹🇭 +66</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+82">🇰🇷 +82</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+55">🇧🇷 +55</option>
                    <option value="+52">🇲🇽 +52</option>
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+234">🇳🇬 +234</option>
                    <option value="+880">🇧🇩 +880</option>
                    <option value="+94">🇱🇰 +94</option>
                    <option value="+977">🇳🇵 +977</option>
                    <option value="+92">🇵🇰 +92</option>
                  </select>
                  <div className="relative flex-1">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                      data-testid="signup-phone-input"
                      placeholder="Phone number (digits only)"
                      type="tel"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "")); setPhoneToken(""); setPhoneOtpSent(false); }}
                      disabled={!!phoneToken}
                      maxLength={14}
                      className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm disabled:opacity-60"
                    />
                  </div>
                </div>
                {!phoneToken && (
                  <button type="button" onClick={sendPhoneOtp} disabled={phoneSending} data-testid="phone-send-otp-btn"
                    className="w-full py-2 rounded-md text-[11px] font-bold bg-[#00b4d8]/15 border border-[#00b4d8]/40 text-[#00b4d8] hover:bg-[#00b4d8]/25 disabled:opacity-40">
                    {phoneSending ? "Sending…" : phoneOtpSent ? "Resend Phone OTP" : "Send Phone OTP"}
                  </button>
                )}
                {phoneOtpSent && !phoneToken && (
                  <div className="flex gap-2">
                    <Input data-testid="phone-otp-input" placeholder="6-digit OTP" maxLength={6} value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ""))}
                      className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm font-mono tracking-[0.4em]" />
                    <button type="button" onClick={verifyPhoneOtp} disabled={phoneVerifying || phoneOtp.length !== 6} data-testid="phone-verify-btn"
                      className="px-3 py-2 rounded-md text-[11px] font-bold bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 disabled:opacity-40">
                      {phoneVerifying ? "Verifying…" : "Verify"}
                    </button>
                  </div>
                )}
                {phoneOtpSent && !phoneToken && phoneSecondsLeft > 0 && (
                  <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Clock size={10} /> OTP expires in {Math.floor(phoneSecondsLeft/60)}:{String(phoneSecondsLeft%60).padStart(2,"0")}</p>
                )}
                {phoneToken && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-300">
                    <CheckCircle size={12} weight="fill" /> Phone verified
                  </div>
                )}

                {/* WhatsApp */}
                <div className="space-y-1.5 pt-2 border-t border-zinc-800">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400">WhatsApp Number</p>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-300 cursor-pointer select-none">
                    <input type="checkbox" data-testid="whatsapp-same-checkbox" checked={whatsappSame} onChange={(e) => setWhatsappSame(e.target.checked)} className="w-3.5 h-3.5 accent-sky-400" />
                    <WhatsappLogo size={12} className="text-emerald-400" />
                    Same as my phone number
                  </label>
                  {!whatsappSame && (
                    <div className="relative">
                      <WhatsappLogo size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500" />
                      <Input data-testid="whatsapp-input" placeholder="WhatsApp number" type="tel" value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)}
                        className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setStep(citizenship === "non_indian" ? 0 : 1)} className="px-3 py-2 rounded-md text-[11px] border border-zinc-800 text-zinc-400 hover:text-zinc-100 flex items-center gap-1">
                    <ArrowLeft size={11} weight="bold" /> Back
                  </button>
                  <button type="button" onClick={() => setStep(3)} disabled={!phoneToken || (!whatsappSame && !whatsappNumber)} data-testid="signup-next-step-2"
                    className="flex-1 py-2 rounded-md text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-40 flex items-center justify-center gap-2">
                    Continue to Email <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: EMAIL */}
            {step === 3 && (
              <div className="space-y-3" data-testid="signup-step-email">
                <div className="relative">
                  <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="signup-email-input" type="email" placeholder="Gmail / Email address" value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailToken(""); setEmailOtpSent(false); }}
                    disabled={!!emailToken}
                    className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm disabled:opacity-60" />
                </div>
                {!emailToken && (
                  <button type="button" onClick={sendEmailOtp} disabled={emailSending} data-testid="email-send-otp-btn"
                    className="w-full py-2 rounded-md text-[11px] font-bold bg-[#00b4d8]/15 border border-[#00b4d8]/40 text-[#00b4d8] hover:bg-[#00b4d8]/25 disabled:opacity-40">
                    {emailSending ? "Sending…" : emailOtpSent ? "Resend Email OTP" : "Send Email OTP"}
                  </button>
                )}
                {emailOtpSent && !emailToken && (
                  <div className="flex gap-2">
                    <Input data-testid="email-otp-input" placeholder="6-digit OTP" maxLength={6} value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                      className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm font-mono tracking-[0.4em]" />
                    <button type="button" onClick={verifyEmailOtp} disabled={emailVerifying || emailOtp.length !== 6} data-testid="email-verify-btn"
                      className="px-3 py-2 rounded-md text-[11px] font-bold bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 disabled:opacity-40">
                      {emailVerifying ? "Verifying…" : "Verify"}
                    </button>
                  </div>
                )}
                {emailOtpSent && !emailToken && emailSecondsLeft > 0 && (
                  <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Clock size={10} /> OTP expires in {Math.floor(emailSecondsLeft/60)}:{String(emailSecondsLeft%60).padStart(2,"0")}</p>
                )}
                {emailToken && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-300">
                    <CheckCircle size={12} weight="fill" /> Email verified
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setStep(2)} className="px-3 py-2 rounded-md text-[11px] border border-zinc-800 text-zinc-400 hover:text-zinc-100 flex items-center gap-1">
                    <ArrowLeft size={11} weight="bold" /> Back
                  </button>
                  <button type="button" onClick={() => setStep(4)} disabled={!emailToken} data-testid="signup-next-step-3"
                    className="flex-1 py-2 rounded-md text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-40 flex items-center justify-center gap-2">
                    Continue to Profile <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: PROFILE */}
            {step === 4 && (
              <div className="space-y-2.5" data-testid="signup-step-profile">
                {citizenship === "non_indian" && (
                  <>
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-400/30 mb-1" data-testid="non-indian-notice">
                      <p className="text-[11px] text-amber-200 leading-relaxed">
                        🌍 Non-Indian registration · Aadhaar is skipped · <span className="font-bold">Affiliate program coming soon</span>. Enter your legal name and country below.
                      </p>
                    </div>
                    <div className="relative">
                      <IdentificationCard size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <Input data-testid="signup-fullname-input" placeholder="Full legal name (as on passport)"
                        value={fullName} onChange={(e) => setFullName(e.target.value)}
                        className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
                    </div>
                    <div className="relative">
                      <Briefcase size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <Input data-testid="signup-country-input" placeholder="Country of citizenship (e.g. United States)"
                        value={country} onChange={(e) => setCountry(e.target.value)}
                        className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
                    </div>
                  </>
                )}
                <div className="relative">
                  <Briefcase size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <select data-testid="signup-work-select" value={work} onChange={(e) => setWork(e.target.value)} required
                    className="w-full pl-9 pr-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="" className="bg-zinc-950">What do you do?</option>
                    {WORK_OPTIONS.map((w) => <option key={w.value} value={w.value} className="bg-zinc-950">{w.label}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <ChartLineUp size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <select data-testid="signup-experience-select" value={tradingExp} onChange={(e) => setTradingExp(e.target.value)} required
                    className="w-full pl-9 pr-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="" className="bg-zinc-950">Trading experience</option>
                    {TRADING_EXP_OPTIONS.map((t) => <option key={t.value} value={t.value} className="bg-zinc-950">{t.label}</option>)}
                  </select>
                </div>

                {citizenship === "indian" && (
                  <div className="relative">
                    <Bank size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input data-testid="signup-upi-input" placeholder="UPI ID (e.g. yourname@upi)" value={upiId} onChange={(e) => setUpiId(e.target.value.trim())}
                      className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
                  </div>
                )}

                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="signup-password-input" type={showSignupPwd ? "text" : "password"} placeholder="Password (8+ chars, mixed case, number, symbol)" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-10 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" minLength={8} />
                  <button
                    type="button"
                    onClick={() => setShowSignupPwd((p) => !p)}
                    tabIndex={-1}
                    aria-label={showSignupPwd ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {showSignupPwd ? <EyeSlash size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                {citizenship === "indian" && (
                  <div>
                    <Input data-testid="signup-referral-input" placeholder={defaultRef ? `Referral Code (default: ${defaultRef})` : "Referral Code (required)"}
                      value={useDefaultRef && defaultRef ? defaultRef : referral} onChange={(e) => setReferral(e.target.value.toUpperCase())}
                      disabled={useDefaultRef && !!defaultRef}
                      className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm disabled:opacity-60" />
                    <label className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer">
                      <input type="checkbox" data-testid="register-no-referral-checkbox" checked={useDefaultRef} onChange={(e) => setUseDefaultRef(e.target.checked)} className="w-3.5 h-3.5 accent-sky-400" />
                      I don&apos;t have a referral code (use default)
                    </label>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setStep(3)} className="px-3 py-2 rounded-md text-[11px] border border-zinc-800 text-zinc-400 hover:text-zinc-100 flex items-center gap-1">
                    <ArrowLeft size={11} weight="bold" /> Back
                  </button>
                  <button type="button" onClick={() => setStep(5)}
                    disabled={
                      !work || !tradingExp || password.length < 8 ||
                      (citizenship === "indian" && (!validUpi(upiId) || (!useDefaultRef && !referral))) ||
                      (citizenship === "non_indian" && (!fullName.trim() || !country.trim()))
                    }
                    data-testid="signup-next-step-4"
                    className="flex-1 py-2 rounded-md text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-40 flex items-center justify-center gap-2">
                    Continue to Payment <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 5: T&C + PAY */}
            {step === 5 && (
              <div className="space-y-3" data-testid="signup-step-pay">
                <div className="p-3 rounded-lg bg-sky-500/10 border border-sky-400/30">
                  <p className="text-[11px] font-bold text-sky-200">Final review</p>
                  <div className="mt-2 space-y-1 text-[11px]">
                    <div className="flex justify-between"><span className="text-zinc-500">Name</span><span className="text-zinc-100 font-mono">{autofill?.name}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Age / Sex</span><span className="text-zinc-100 font-mono">{autofill?.age} / {autofill?.sex}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Phone</span><span className="text-zinc-100 font-mono">{phone}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Email</span><span className="text-zinc-100 font-mono truncate ml-2">{email}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">UPI</span><span className="text-zinc-100 font-mono">{upiId}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Work · Experience</span><span className="text-zinc-100">{WORK_OPTIONS.find(w=>w.value===work)?.label} · {TRADING_EXP_OPTIONS.find(t=>t.value===tradingExp)?.label}</span></div>
                  </div>
                </div>

                {/* T&C checkbox — no bullet text, just the acceptance tick */}
                <label className="flex items-start gap-2 text-[11px] text-zinc-200 cursor-pointer select-none p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                  <input type="checkbox" data-testid="accept-terms-checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="w-3.5 h-3.5 accent-sky-400 mt-0.5" />
                  <span>I have read and accept the <a href="/legal" target="_blank" rel="noreferrer" className="text-sky-400 underline underline-offset-2">Terms &amp; Conditions</a> and authorize the {citizenship === "non_indian" ? "$99 USD" : "₹2,999"} one-time platform fee.</span>
                </label>

                {/* Sponsor-inactive banner (address slot ladder rejection) */}
                {addressLock && (
                  <div
                    data-testid="address-lock-banner"
                    className="p-3 rounded-lg text-[11px]"
                    style={{
                      background: "rgba(245,158,11,0.08)",
                      border: "1px solid rgba(245,158,11,0.35)",
                      color: "#fcd34d",
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-amber-500 text-black">
                        Sponsor Not Active
                      </span>
                      {addressLock.holder_display_id && (
                        <span className="font-mono text-white/80">
                          {addressLock.holder_display_id}
                        </span>
                      )}
                      {addressLock.holder_referral_code && (
                        <span className="font-mono text-amber-200/70">
                          {addressLock.holder_referral_code}
                        </span>
                      )}
                    </div>
                    <p className="text-white/90 leading-relaxed">
                      {addressLock.code === "ADDRESS_SLOT_LOCKED" && (
                        <>
                          The affiliate at this address currently has{" "}
                          <b>{addressLock.holder_directs} direct referral{addressLock.holder_directs === 1 ? "" : "s"}</b>.
                          They need to reach <b>{addressLock.next_threshold}</b> directs
                          to unlock slot <b>#{(addressLock.slots_used || 1) + 1}</b> at this address
                          {addressLock.slots_at_next ? ` (of ${addressLock.slots_at_next} max at that tier)` : ""}.
                          {" "}Their affiliate ID is <b>not yet active</b> for family expansion.
                        </>
                      )}
                      {addressLock.code === "ADDRESS_REF_MISMATCH" && (
                        <>The referral code you entered does not match the account-holder registered at this address. Only the primary holder&apos;s referral can be used to add additional family members.</>
                      )}
                      {addressLock.code === "ADDRESS_UNPAID_HOLDER" && (
                        <>The existing account-holder has not paid their own ₹2,999 platform fee yet — they must complete their signup before referring anyone at this address.</>
                      )}
                      {addressLock.code === "ADDRESS_MAX_REACHED" && (
                        <>This address has already reached the maximum of 4 registered accounts (16-direct ceiling). No further family signups are possible here.</>
                      )}
                      {addressLock.code === "ADDRESS_LADDER_INACTIVE_SPONSOR" && (
                        <>This address already has a registered account. You must sign up via that holder&apos;s referral link to be added as a family member.</>
                      )}
                    </p>

                    {addressLock.code !== "ADDRESS_MAX_REACHED" && (
                      <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(245,158,11,0.25)" }}>
                        <label className="flex items-start gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            data-testid="drop-ref-checkbox"
                            checked={acceptDropRef}
                            onChange={(e) => setAcceptDropRef(e.target.checked)}
                            className="w-3.5 h-3.5 accent-emerald-400 mt-0.5"
                          />
                          <span className="text-white/85">
                            <b>I don&apos;t have a referral code.</b> Continue my registration as an independent user (not tied to this family).
                          </span>
                        </label>
                        {acceptDropRef && (
                          <button
                            type="button"
                            data-testid="continue-without-ref-btn"
                            onClick={() => {
                              setReferral("");
                              setUseDefaultRef(true);
                              setAddressLock(null);
                              setError("");
                              // Immediately retry signup with fallback referral
                              handlePayAndRegister();
                            }}
                            className="mt-2 w-full py-2 rounded-md text-[11px] font-bold text-black"
                            style={{ background: "#06d6a0" }}
                          >
                            Continue Without Referral →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button type="button" onClick={() => setStep(4)} className="px-3 py-2 rounded-md text-[11px] border border-zinc-800 text-zinc-400 hover:text-zinc-100 flex items-center gap-1">
                    <ArrowLeft size={11} weight="bold" /> Back
                  </button>
                  <button type="button" onClick={handlePayAndRegister} disabled={loading || !acceptedTerms} data-testid="signup-pay-btn"
                    className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-[#00b4d8] text-[#0b132b] hover:bg-[#0077b6] hover:text-white disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <>Pay {citizenship === "non_indian" ? "$99" : "₹2,999"} & Register <ArrowRight size={14} weight="bold" /></>}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <p className="text-center text-xs text-zinc-500 mt-5">
          {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
          <button onClick={() => { setIsRegister(!isRegister); setError(""); setStep(0); setCitizenship(""); }} data-testid="toggle-auth-mode" className="text-blue-400 hover:text-blue-300 font-medium">
            {isRegister ? "Sign In" : "Register"}
          </button>
        </p>
      </div>
    </div>
  );
}
